/**
 * Coming back to change an answer (F5).
 *
 * WHY THE CHECK, RATHER THAN AN EDITOR IN THE APP. Twenty-seven answers, half
 * of them conditional on another, and every question already written and
 * reviewed here. Rebuilding that inside the workspace would mean a second copy
 * of every question — which drifts, and which puts us one careless edit away
 * from a rules statement no adviser has signed off (CLAUDE.md rule 10). So "change an
 * answer" brings somebody back to the questions they already answered, with
 * their answers in the boxes.
 *
 * THE ONE ANSWER THAT CANNOT BE PUT BACK, and this is a feature.
 * `sponsor_receives_benefit` is stored as a plain boolean because WHICH
 * disability or carer's benefit somebody receives is health data — special
 * category under Article 9 — and ADR-0023 reduces it at the question rather
 * than storing it. So a returning person's chips cannot be re-ticked: we do not
 * know which one they picked, by design. They are told that, and asked again.
 *
 * Anonymous visitors are unaffected. Everything here is behind a request that
 * a signed-out browser simply fails, and failing means the ordinary blank check.
 */
import type { WizardAnswers } from "@verity/intake";

export interface ExistingCase {
  answers: WizardAnswers;
  version: number;
}

/**
 * Why a signed-in person is here.
 *
 * "setup" — they are partway through sign-up and the check is a STEP in it.
 * "revising" — their case is set up and they came back to change an answer.
 *
 * The two want different words and a different button, and neither wants the
 * page a stranger sees. Conflating them is what produced a check that asked a
 * signed-up customer for their name and email, and offered to "Start your
 * application" to somebody who had already started one.
 */
export type CheckContext = "setup" | "revising";

export interface SignedInVisit {
  context: CheckContext;
  /** Null when they have not taken the check yet — the F6 path. */
  existing: ExistingCase | null;
}

/**
 * The case this browser is signed in to, or null.
 *
 * Null covers signed out, no case yet, and the request failing — all of which
 * mean the same thing here: show a blank check.
 */
export async function loadExistingCase(fetchImpl: typeof fetch = fetch): Promise<ExistingCase | null> {
  try {
    const response = await fetchImpl("/api/cases/current", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { answers?: WizardAnswers; version?: number };
    if (!body.answers) return null;
    return { answers: body.answers, version: body.version ?? 1 };
  } catch {
    return null;
  }
}

/**
 * Who this is, and why they are here — or null for the anonymous visitor the
 * check was built for, which is almost everybody.
 *
 * Two requests rather than one because "do you have a case" and "have you
 * finished setting up" are genuinely different questions, and somebody
 * mid-setup has an answer to the second and not the first.
 */
export async function loadSignedInVisit(
  fetchImpl: typeof fetch = fetch,
): Promise<SignedInVisit | null> {
  let onboarding: { mine_completed_at: string | null };
  try {
    const response = await fetchImpl("/api/onboarding", {
      headers: { accept: "application/json" },
    });
    // 401 is the anonymous visitor. 404 is somebody with no household yet.
    // Either way: the ordinary public check.
    if (!response.ok) return null;
    onboarding = (await response.json()) as { mine_completed_at: string | null };
  } catch {
    return null;
  }

  return {
    context: onboarding.mine_completed_at ? "revising" : "setup",
    existing: await loadExistingCase(fetchImpl),
  };
}

/** Answer field → the control name used in the form. */
const CONTROL: Record<string, string> = {
  sponsor_status: "sponsor_status",
  stage: "stage",
  intended_date: "intended_date",
  first_applied_before_cutoff: "first_before_cutoff",
  granted_on_first_application: "granted_first",
  holds_five_year_route_permission: "holds_permission",
  applying_with_same_partner: "same_partner",
  relationship_type: "relationship_type",
  date_of_marriage: "date_of_marriage",
  marriage_legally_valid: "marriage_valid",
  cohabiting_since: "cohabiting_since",
  both_over_18: "both_over_18",
  met_in_person: "met_in_person",
  within_prohibited_degrees: "prohibited_degrees",
  previous_relationships_ended: "previous_ended",
  route: "route",
  income_from: "income_from",
  months_with_employer: "months_with_employer",
  gross_annual: "gross_annual",
  savings_amount: "savings_amount",
  savings_held_since: "savings_held_since",
  english_exemption: "english_exemption",
  english_level: "english_level",
  legal_right_to_occupy: "right_to_occupy",
  without_public_funds: "without_public_funds",
  life_in_uk_passed: "life_in_uk",
  dependent_children: "dependent_children",
};

/** The inverse of `yesNo`: null is "not sure", which is a real answer. */
function triboolValue(value: boolean | null): string {
  return value === null ? "unsure" : value ? "yes" : "no";
}

/**
 * Put saved answers back into the form.
 *
 * Returns whether the benefit question needs re-answering, so the caller can
 * say so rather than leaving somebody to notice a silently blank question.
 *
 * Every write dispatches `change`, because the form's conditional logic hangs
 * off those events — setting `stage` without one leaves the in-country
 * questions hidden and their answers unreachable.
 */
export function prefill(form: HTMLFormElement, answers: WizardAnswers): { benefitNeedsReasking: boolean } {
  for (const [answerKey, controlName] of Object.entries(CONTROL)) {
    const value = (answers as unknown as Record<string, unknown>)[answerKey];
    if (value === undefined) continue;

    const controls = form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
      `[name="${controlName}"]`,
    );
    if (controls.length === 0) continue;

    const first = controls[0]!;
    const asString =
      typeof value === "boolean" && answerKey !== "dependent_children"
        ? triboolValue(value)
        : value === null
          ? answerKey === "dependent_children"
            ? ""
            : "unsure"
          : typeof value === "boolean"
            ? value
              ? "yes"
              : "no"
            : String(value);

    if (first instanceof HTMLInputElement && (first.type === "radio" || first.type === "checkbox")) {
      const options = [...controls] as HTMLInputElement[];
      const match = options.some((input) => input.value === asString);

      /**
       * A REAL ANSWER THE FORM DOES NOT OFFER IS LEFT ALONE, NOT ERASED.
       *
       * Setting `checked` across the group unchecks every button when nothing
       * matches. For a NULL answer that is correct — a yes/no group with no
       * "unsure" option represents "not sure" by having nothing selected, and
       * `life_in_uk` is exactly that.
       *
       * For a non-null value it is destruction. It means the stored answer and
       * the form have diverged, and the response to that must not be to wipe
       * what somebody told us and let them discover a blank question they have
       * already filled in. Skipping leaves the group untouched; the wizard's
       * own validation then asks for it properly.
       */
      if (!match && value !== null) continue;

      for (const input of options) input.checked = input.value === asString;
    } else {
      (first as HTMLInputElement).value = asString;
    }
    first.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // See the header. We hold a boolean and never the benefit, so a "yes" cannot
  // be turned back into a ticked chip.
  return { benefitNeedsReasking: answers.sponsor_receives_benefit === true };
}

/**
 * Record the revised answers as a new version of the case.
 *
 * Appends — `case_versions` has held SELECT and INSERT and no UPDATE since F2,
 * so the previous version survives whatever happens here. That is the whole
 * point of §9.1: what somebody was told before is still on the record after
 * they change their mind.
 */
export async function saveRevision(
  answers: WizardAnswers,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetchImpl("/api/cases", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ answers }),
    });
    return response.ok;
  } catch {
    return false;
  }
}
